import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    ViewEncapsulation,
} from '@angular/core';
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VarDirective } from '@backoffice-frontend/shared/directives/common';
import { type SettingSpotConfiguration } from '@clean-code/models';
import { EnergySourceDropdownComponent } from '@clean-code/shared/bo/components/ui-energy-source-dropdown';
import { EnumService } from '@clean-code/shared/bo/util-enum';
import { indicate } from '@clean-code/shared/common';
import { ActionButtonModule } from '@clean-code/shared/components/ui-action-button';
import { CanDeactivateForm } from '@clean-code/shared/components/ui-can-deactivate';
import { FormContainerModule } from '@clean-code/shared/components/ui-form-container';
import { PageContainerModule } from '@clean-code/shared/components/ui-page-container';
import { PipesModule } from '@clean-code/shared/ui-pipes';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFileExcel, faList } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { first } from 'rxjs/operators';
import { CountryDropdownComponent } from 'shared/bo/components/ui-country-dropdown';
import { SettingSpotConfigurationService } from './../../services/setting-spot-configuration.service';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'backoffice-frontend-setting-spot-configuration-details',
    templateUrl: './setting-spot-configuration-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: CanDeactivateForm,
            useExisting: forwardRef(() => SettingSpotConfigurationDetailsComponent),
        },
    ],
    imports: [
        CommonModule,
        TranslocoModule,
        MatTooltipModule,
        MatCheckboxModule,
        PageContainerModule,
        FormContainerModule,
        ActionButtonModule,
        PipesModule,
        MatSlideToggleModule,
        MatSelectModule,
        NgSelectModule,
        EnergySourceDropdownComponent,
        VarDirective,
        CountryDropdownComponent,
        FontAwesomeModule,
    ]
})
export class SettingSpotConfigurationDetailsComponent extends CanDeactivateForm<SettingSpotConfiguration> {
  public faList = faList;
  public faExcel = faFileExcel;

  formGroup = new UntypedFormGroup({
    id: new UntypedFormControl(null),
    country: new UntypedFormControl(null, [Validators.required]),
    energySource: new UntypedFormControl(null, [Validators.required]),
    stockMarket: new UntypedFormControl(null, [Validators.required]),
  });

  constructor(
    private enumService: EnumService,
    settingSpotConfigurationService: SettingSpotConfigurationService
  ) {
    super(settingSpotConfigurationService);
  }

  protected override add(): void {
    this.service
      .add$(this.formValue)
      .pipe(first(), indicate(this.isLoading$))
      .subscribe(() => {
        this.formGroup.markAsPristine();
        this.toastService.showSuccess();
        this.tableStateService?.refresh();
      });
  }

  protected override update(): void {
    this.service
      .update$(this.formValue)
      .pipe(first(), indicate(this.isLoading$))
      .subscribe(() => {
        this.formGroup.markAsPristine();
        this.toastService.showSuccess();
        this.tableStateService?.refresh();
      });
  }

  public spotConfigurationIdentifier() {
    const v = this.formGroup.getRawValue();
    void this.router.navigate([
      '/spotConfigurationIdentifier',
      v.id,
      v.energySource,
      v.country,
      v.stockMarket,
    ]);
  }

  public excelExport() {
    const id = this.formGroup.get('id')?.value;
    if (id == null) {
      return;
    }
    (this.service as SettingSpotConfigurationService)
      .exportSpotConfgiurationResultToExcel$(id)
      .pipe(first(), indicate(this.isLoading$))
      .subscribe();
  }
}
